import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import type { WordCardKind } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import {
  ensureUserCardRows,
  startUserCardSchedule,
} from "@-/dev.improve.haniverse.lib.cards.user-card.rows";
import { and, eq, isNull } from "@-/com.npmjs.drizzle-orm";

export type CreateUserWordCardArgs = {
  actorDb: ActorDatabase;
  /** Which skill this card drills. Part of the card's identity — see {@link WORD_CARD_KIND_LIST}. */
  kind: WordCardKind;
  /** Tone-number pinyin for the whole word — multi-syllable for a multi-character word. */
  pinyin: string;
  startedStudyingAt?: string;
  word: string;
};

/**
 * Introduce a word into the user's deck as a sentence-context card.
 *
 * Deliberately a separate routine from `createUserCard` rather than a branch inside it. That
 * function is grapheme-atomic: it requires an `app_grapheme_meaning_card` row (throwing without
 * one), creates a fixed meaning + pronunciations bundle, and gates on a Hanzi character
 * prevalence check (`getGraphemePrevalence`, `twUsageMap`) that is meaningless for a
 * multi-character word. None of that applies here — a word card has no content-catalog row at
 * all; its prompt is assembled at review time from `sentence` + `sentence_word`.
 *
 * Idempotent on `(word, pinyin, kind)`: a card that is already in study is returned as-is, and
 * a soft-deleted one comes back as a new card rather than a duplicate. The rows carry ids derived
 * from that triple (`ensureUserCardRows`), so two devices that add the same word before they sync
 * write the same rows.
 */
export async function createUserWordCard({
  actorDb,
  kind,
  pinyin,
  startedStudyingAt,
  word,
}: CreateUserWordCardArgs): Promise<{ userCardId: string }> {
  return actorDb(import.meta.location).transaction(async (transaction) => {
    const now = new Date().toISOString();
    // `kind` is part of the identity: without it, creating (say) the spoken card for a word that
    // already has a characters-in-context card would resolve to that OTHER card, and the requested
    // card would silently never be created.
    const { ids } = await ensureUserCardRows(
      transaction,
      { kind: "word", pinyin, word, wordCardKind: kind },
      now,
    );
    const card = await transaction.query.userCard.findFirst({
      columns: { fsrsCardId: true },
      where: { id: ids.userCardId },
    });
    if (card === undefined) {
      throw new Error(`Card rows for the word '${word}' were not written`);
    }
    if (card.fsrsCardId !== null) {
      return { userCardId: ids.userCardId };
    }

    // A brand-new card is due immediately so it enters the rotation on the next scheduler pass.
    await startUserCardSchedule(transaction, {
      dueIso: startedStudyingAt ?? now,
      fsrsCardId: ids.fsrsCardId,
      nowIso: now,
      scheduledDays: 0,
    });
    await transaction
      .update(actorSchema.userCard)
      .set({ fsrsCardId: ids.fsrsCardId, startedStudyingAt: startedStudyingAt ?? now })
      .where(eq(actorSchema.userCard.id, ids.userCardId));

    return { userCardId: ids.userCardId };
  });
}

/**
 * The reading a word is already enrolled under, or `null` for a word never added.
 *
 * A word card's identity is `(word, pinyin, kind)`. Once a word is in the deck, THIS is its
 * reading: re-adding it must reuse it rather than re-deriving one from the dictionary, whose
 * definition order a static rebuild can change (which would enroll a second, duplicate set of
 * cards with separate schedules). Tombstoned rows count too, so reset → re-add resurrects the
 * same cards. A live row wins over a tombstoned one.
 */
export async function findEnrolledWordPinyin({
  actorDb,
  word,
}: {
  actorDb: ActorDatabase;
  word: string;
}): Promise<string | null> {
  return actorDb(import.meta.location).transaction(async (transaction) => {
    // `queryRaw`: a tombstoned row still fixes the word's reading (see above).
    const rows = await transaction.queryRaw.userWordSentenceCard.findMany({
      columns: { deletedAt: true, pinyin: true },
      orderBy: { createdAt: "asc" },
      where: { word },
    });
    return (rows.find((row) => row.deletedAt === null) ?? rows[0])?.pinyin ?? null;
  });
}

/** Soft-delete a word card, tombstoning both the overlay and its `user_card`. */
export async function removeUserWordCard({
  actorDb,
  kind,
  pinyin,
  word,
}: {
  actorDb: ActorDatabase;
  kind: WordCardKind;
  pinyin: string;
  word: string;
}): Promise<void> {
  await actorDb(import.meta.location).transaction(async (transaction) => {
    // Keyed on `kind` too — see the note in `createUserWordCard`; without it this would tombstone
    // whichever of the word's cards the lookup happened to return first.
    const existing = await transaction.queryRaw.userWordSentenceCard.findFirst({
      where: { kind, pinyin, word },
    });
    if (existing === undefined || existing.deletedAt !== null) {
      return;
    }
    const now = new Date().toISOString();
    await transaction
      .update(actorSchema.userWordSentenceCard)
      .set({ deletedAt: now, updatedAt: now })
      .where(eq(actorSchema.userWordSentenceCard.id, existing.id));
    await transaction
      .update(actorSchema.userCard)
      .set({ deletedAt: now, updatedAt: now })
      .where(
        and(
          eq(actorSchema.userCard.id, existing.userCardId),
          isNull(actorSchema.userCard.deletedAt),
        ),
      );
  });
}
